import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Search, RefreshCw, Eye, FileText, Clock, CheckCircle } from 'lucide-react';
import NumuneOnizlemeModal from '../production/NumuneOnizlemeModal';
import { BaskiGorselComponent } from '../production/BaskiGorselLazyImage';
import { customerPortalService } from '../../services/customerPortalApi';
import toast from 'react-hot-toast';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import config from '../../config/config';

/**
 * DigitalPrintingNumuneIstekleriTab - Müşteri Portalı Numune İstekleri Tabı
 * Sadece o müşterinin aktif ve yolda (Sevk) durumundaki numunelerini gösterir
 * Sadece görüntüleme modu - oluşturma, silme, sevk etme butonları YOK
 */
const DigitalPrintingNumuneIstekleriTab = () => {
  const { customer } = useCustomerAuth();
  const [numuneler, setNumuneler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [durumFilter, setDurumFilter] = useState('Aktif'); // Varsayılan: Aktif
  const [stats, setStats] = useState({
    toplam: 0,
    aktif: 0,
    sevk: 0
  });
  
  // Önizleme modalı state'leri
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedNumune, setSelectedNumune] = useState(null);

  // Numuneleri yükle
  const loadNumuneler = useCallback(async () => {
    if (!customer?.id) {
      return;
    }

    setLoading(true);
    try {
      const response = await customerPortalService.get('/digital-printing/numuneler');
      if (response.data.success) {
        let filteredData = response.data.data || [];
        // Aktif seçiliyken hem Aktif hem de Sevk durumundaki numuneleri göster
        if (durumFilter === 'Aktif') {
          filteredData = filteredData.filter(n => n.durum === 'Aktif' || n.durum === 'Sevk');
        } else if (durumFilter) {
          filteredData = filteredData.filter(n => n.durum === durumFilter);
        }
        setNumuneler(filteredData);
        
        // İstatistikleri hesapla
        const toplam = filteredData.length;
        const aktif = filteredData.filter(n => n.durum === 'Aktif').length;
        const sevk = filteredData.filter(n => n.durum === 'Sevk').length;
        
        setStats({ toplam, aktif, sevk });
      }
    } catch (error) {
      toast.error('Numuneler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [customer?.id, durumFilter]);

  // Component mount olduğunda verileri yükle
  useEffect(() => {
    loadNumuneler();
  }, [loadNumuneler]);

  // Filtreleme fonksiyonları
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDurumChange = (durum) => {
    setDurumFilter(durum);
  };

  const handleShowDetail = (numune) => {
    setSelectedNumune(numune);
    setShowPreviewModal(true);
  };

  function formatNumuneNo(value) {
    return value ? String(value).toUpperCase() : '-';
  }

  function getStatusBadgeClass(durum) {
    if (durum === 'Aktif') {
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    } else if (durum === 'Sevk') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    } else {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    }
  }

  // Görsel URL'lerini işle - Modal'daki processImageUrl mantığıyla aynı
  const processImageUrl = (gorselUrls) => {
    if (!gorselUrls) return null;
    
    let urls = gorselUrls;
    if (typeof urls === 'string') {
      try {
        urls = JSON.parse(urls);
      } catch (e) {
        urls = [urls];
      }
    }
    
    if (Array.isArray(urls) && urls.length > 0) {
      const first = urls[0];
      const cleanUrl = String(first).replace(/^["']+|["']+$/g, '');
      return cleanUrl.startsWith('http') ? cleanUrl : `${config.backendUrl}${cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`}`;
    }
    
    return null;
  };

  // Filtrelenmiş numuneler
  const filteredNumuneler = numuneler.filter(numune => {
    // Arama filtresi
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const numuneNo = (formatNumuneNo(numune.numune_no) || '').toLowerCase();
      const musteriAdi = (numune.musteri_adi || '').toLowerCase();
      const stokKartAdi = (numune.stok_kart_adi || '').toLowerCase();
      const desenNo = (numune.desen?.desen_no || '').toLowerCase();
      
      if (!numuneNo.includes(searchLower) && 
          !musteriAdi.includes(searchLower) && 
          !stokKartAdi.includes(searchLower) &&
          !desenNo.includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  return (
    <div className="space-y-6 -mx-3 sm:-mx-4 lg:-mx-6 px-3 sm:px-4 lg:px-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Numune İstekleri</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <button 
            onClick={loadNumuneler}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 w-full sm:w-auto"
          >
            <RefreshCw className={`h-5 w-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            <span>Yenile</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/20 rounded-md p-3">
              <ClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Toplam</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.toplam}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-orange-100 dark:bg-orange-900/20 rounded-md p-3">
              <Clock className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktif</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.aktif}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-100 dark:bg-green-900/20 rounded-md p-3">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Yolda</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.sevk}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Arama */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Numune no, müşteri, stok kartı ara..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Durum Filtresi */}
          <div className="flex gap-2">
            <button
              onClick={() => handleDurumChange('Aktif')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                durumFilter === 'Aktif'
                  ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-300 dark:border-orange-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Aktif
            </button>
            <button
              onClick={() => handleDurumChange('Sevk')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                durumFilter === 'Sevk'
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Yolda
            </button>
            <button
              onClick={() => handleDurumChange('')}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                durumFilter === ''
                  ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Tümü
            </button>
          </div>
        </div>
      </div>

      {/* Numune Listesi */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw className="h-12 w-12 text-gray-400 dark:text-gray-500 animate-spin" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">Numuneler yükleniyor...</p>
          </div>
        ) : filteredNumuneler.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-gray-500 dark:text-gray-400">Henüz numune bulunmuyor</p>
          </div>
        ) : (
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {filteredNumuneler.map((numune) => {
                const numuneNo = formatNumuneNo(numune.numune_no);
                const imageUrl = processImageUrl(numune.desen?.gorsel_urls);

                const isSevk = numune.durum === 'Sevk';
                
                return (
                  <div
                    key={numune.id}
                    className={`border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${
                      isSevk ? 'bg-green-50 dark:bg-green-900/20' : 'bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div
                      className="relative h-44 bg-gray-100 dark:bg-gray-700 rounded-t-lg overflow-hidden cursor-pointer"
                      onClick={() => handleShowDetail(numune)}
                    >
                      {imageUrl ? (
                        <BaskiGorselComponent
                          imageUrl={imageUrl}
                          alt={numune.desen?.desen_no || 'Numune Deseni'}
                          useThumbnail
                          priority={false}
                        />
                      ) : null}
                      {/* Fallback for no image */}
                      <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500" style={{ display: imageUrl ? 'none' : 'flex' }}>
                        <div className="text-center">
                          <FileText className="h-16 w-16 mx-auto mb-2" />
                          <p className="text-sm">Resim Yok</p>
                        </div>
                      </div>

                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(numune.durum)}`}>
                          {numune.durum}
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs font-medium px-2 py-1 rounded">
                        {new Date(numune.created_at).toLocaleDateString('tr-TR')}
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Numune No</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">{numuneNo}</p>
                        </div>
                        <button
                          onClick={() => handleShowDetail(numune)}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs font-medium"
                        >
                          Detay
                          <Eye className="h-4 w-4 ml-1" />
                        </button>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-500 dark:text-gray-400">Müşteri</span>
                          <span className="ml-2 truncate text-right">{numune.musteri_adi}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-500 dark:text-gray-400">Stok Kartı</span>
                          <span className="ml-2 truncate text-right">{numune.stok_kart_adi || 'N/A'}</span>
                        </div>
                        {numune.desen?.desen_no && (
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-500 dark:text-gray-400">Desen</span>
                            <span className="ml-2 truncate text-right">{numune.desen.desen_no}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Numune Önizleme Modal */}
      {selectedNumune && (
        <NumuneOnizlemeModal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedNumune(null);
          }}
          numune={selectedNumune}
        />
      )}
    </div>
  );
};

export default DigitalPrintingNumuneIstekleriTab;
